import {
  CONTROL_LOCK_HEARTBEAT_MIN_MS,
  CONTROL_LOCK_HEARTBEAT_RATIO,
  CONTROL_LOCK_TTL_FALLBACK_MS,
} from '@ctcells/core'
import { useEffect, useSyncExternalStore } from 'react'

import { heartbeatControlLock } from './commands/controlLock.ts'
import {
  expireControlLockIfDue,
  getControlLockSnapshot,
  markControlLockDenied,
  setControlLease,
  subscribeControlLock,
  type ControlLockSnapshot,
} from './controlToken.ts'

/**
 * 제어락 상태를 읽는 **유일한 훅**.
 *
 * 🔴 **버튼 활성의 근거로 이 값 하나를 쓰지 않는다.** 활성은 서버가 준 `allowed_commands`
 *    이고(백엔드가 `holds_lock` 을 이미 반영해 준다), 이 값은 **비활성 사유를 고르는 축**이다.
 *
 * ```ts
 * const lock = useControlLock()
 * lock.held          // 내가 쥐고 있고 아직 살아 있다
 * lock.status        // 'none' | 'mine' | 'expired' | 'other'
 * lock.holder        // 서버가 준 보유자 표기. 모르면 undefined
 * lock.expiresAt     // 서버가 준 만료 시각(ISO). 카운트다운은 만들지 않는다
 * ```
 */
export interface ControlLockState extends ControlLockSnapshot {
  /** `status === 'mine'`. 화면이 `status` 를 다시 해석하지 않게 미리 접어 둔다 */
  held: boolean
}

export function useControlLock(): ControlLockState {
  const snapshot = useSyncExternalStore(
    subscribeControlLock,
    getControlLockSnapshot,
    getControlLockSnapshot,
  )
  return { ...snapshot, held: snapshot.status === 'mine' }
}

/**
 * 갱신 간격(ms). **리스가 정한다** — 서버 TTL 설정이 바뀌면 따라 움직인다.
 * 시각을 못 읽은 리스는 폴백 TTL 을 쓴다(정본 `IFC-COM-04` 인용).
 */
export function heartbeatIntervalMs(expiresAtMs: number, now: number): number {
  const remaining = expiresAtMs - now
  const lifetime = remaining > 0 ? remaining : CONTROL_LOCK_TTL_FALLBACK_MS
  return Math.max(
    CONTROL_LOCK_HEARTBEAT_MIN_MS,
    Math.floor(lifetime * CONTROL_LOCK_HEARTBEAT_RATIO),
  )
}

/**
 * 리스 갱신 루프. **앱 셸이 한 번만 단다**(`AppRuntime`) — 화면이 달지 않는다.
 *
 * 🔴 **주기 폴링(DP-01)이 아니다.** 상태 조회가 아니라 **내가 쥔 리스의 수명을 미는 명령**이고,
 *    락을 쥐지 않으면 요청이 **0건**이다. ESLint 의 폴링 금지 규칙은 `refetchInterval` 류
 *    쿼리 옵션을 막는 것이라 여기 걸리지 않는다 — 규칙을 끄거나 우회하지 않았다.
 * 🔴 **실패하면 즉시 놓는다.** 한 번의 실패로도 보관소를 `expired` 로 내려 버튼이 닫힌다 —
 *    「아직 내 것이겠지」로 버티면 만료된 토큰으로 장비를 조작하려 든다.
 * 🔴 **탭이 뒤로 가면 타이머가 늘어진다**(브라우저 스로틀). TTL 30초에서는 치명적이라 셋으로 막는다.
 *    ⓐ 간격을 수명의 1/3 로 잡아 **2회를 놓쳐도** 아직 만료 전이다
 *    ⓑ 화면이 다시 보이면(`visibilitychange`) **즉시 한 번** 민다
 *    ⓒ 그래도 수명이 다하면 서버에 묻기 전에 **먼저 닫는다**(`expireControlLockIfDue`) —
 *       늦게 깬 타이머가 「성공했겠지」로 버튼을 열어 두지 않는다
 */
export function useControlLockHeartbeat(): void {
  const { status, lease } = useControlLock()
  const expiresAtMs = lease?.expiresAtMs ?? 0

  useEffect(() => {
    // 락이 없으면 타이머 자체가 없다 — 이 조건이 「폴링이 아니다」의 집행 지점이다
    if (status !== 'mine') return

    let alive = true
    let timer: ReturnType<typeof setTimeout> | undefined
    let inFlight = false

    const arm = (delay: number) => {
      if (!alive) return
      if (timer !== undefined) clearTimeout(timer)
      timer = setTimeout(() => void beat(), Math.max(0, delay))
    }

    const beat = async () => {
      if (!alive || inFlight) return
      // 수명이 이미 다했으면 **묻기 전에 닫는다**(스로틀로 늦게 깬 경우)
      expireControlLockIfDue()
      if (getControlLockSnapshot().status !== 'mine') return
      const token = getControlLockSnapshot().lease?.token
      inFlight = true
      try {
        const lease = await heartbeatControlLock()
        if (!alive) return
        setControlLease(lease, Date.now(), 'heartbeat')
      } catch {
        // 🔴 409 는 클라이언트가 이미 보관소에 반영했다. 그 밖(네트워크·타임아웃)도 **놓는다** —
        //    갱신을 못 했다는 사실 자체가 「내 것이라 믿을 근거가 없다」는 뜻이다.
        if (alive && getControlLockSnapshot().lease?.token === token) markControlLockDenied({})
      } finally {
        inFlight = false
        if (alive && getControlLockSnapshot().status === 'mine') {
          const now = Date.now()
          arm(heartbeatIntervalMs(getControlLockSnapshot().lease?.expiresAtMs ?? now, now))
        }
      }
    }

    const onVisible = () => {
      if (document.visibilityState !== 'visible') return
      void beat()
    }

    arm(heartbeatIntervalMs(expiresAtMs, Date.now()))
    document.addEventListener('visibilitychange', onVisible)
    return () => {
      alive = false
      if (timer !== undefined) clearTimeout(timer)
      document.removeEventListener('visibilitychange', onVisible)
    }
  }, [status, expiresAtMs])
}
